<template>
  <div>
    <HeaderComponent :isAdminPage="true" />
  </div>
  <div v-if="isInitError" id="com_form_contet_admin midlleform">
    <div class="errorform">
      <!-- エラーメッセージの表示エリア -->
      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>
    </div>
  </div>
  <div v-else class="com_form_contet_admin midlleform">
    <loading-overlay v-model:active="isLoading" :is-full-page="true" />
    <section class="com_sec1">
      <div class="inner1">
        <h2 class="title_left">イベント申込み情報ダウンロード</h2>
      </div>
      <!-- エラーメッセージの表示エリア -->
      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>
      <div v-if="isDataFetched">
        <div class="contents1">
          <table>
            <td class="required-mark">※</td>
            <td>イベント</td>
          </table>
          <select
            id="agCode"
            v-model="selectedEventId"
            class="form-control"
            style="width: 850px"
            ref="cmbEvent"
          >
            <option
              v-for="item in eventDataList"
              :key="item.eventId"
              :value="item.eventId"
            >
              {{ item.eventName }}
            </option>
          </select>
          <div class="form-inline flex-container">
            <div class="form-group"></div>
            <div class="form-group" style="margin-left: 510px">
              <button
                type="button"
                name="btnBack"
                class="btn btn-footer btn-primary"
                @click="onBack"
              >
                戻る
              </button>
              <button
                type="button"
                name="btnFixed"
                class="btn btn-footer-large btn-primary"
                @click="onDownloadClick"
              >
                ダウンロード
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
  <div>
    <!-- フッタ -->
    <FooterComponent />
  </div>
  <BackToTop />
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from "vue";
import HeaderComponent from "../layout/HeaderComponent.vue";
import FooterComponent from "../layout/FooterComponent.vue";
import {
  formatMessage,
  outputDebugLogs,
  getTargetDate,
  scrollToTop,
  isTokenAlive,
} from "@/Common";
import { AppConstants } from "@/constants/AppConstants";
import apiAuthClient from "@/util/apiClientAuth";
import apiDownloadClient from "@/util/apiClientDownload";
import { useProcessingStore } from "../../store/useProcessingStore";
import messages from "@/assets/messages.json";
import BackToTop from "../controls/BackToTop.vue";
import { useUserStore } from "@/store/userStore";
import { useRouter } from "vue-router";
import LoadingOverlay from "vue-loading-overlay";
import "vue-loading-overlay/dist/css/index.css";

interface selectItem {
  eventId: string;
  eventName: string;
  eventCsvFileName: string;
}

export default defineComponent({
  name: "EventEntryDownloadComponent",
  components: {
    HeaderComponent,
    FooterComponent,
    BackToTop,
    LoadingOverlay,
  },
  setup() {
    const router = useRouter();
    const isInitError = ref(true); // 初期表示時のエラーフラグ
    const processingStore = useProcessingStore();
    const isDataFetched = ref(false); // データが取得済みかどうかを追跡
    const errorMessage = ref(""); // エラーメッセージ（キーワード検索の下のやつ）
    const isKaunetAdmin = ref(false); // カウネット管理者フラグ
    const eventDataList = ref<selectItem[]>([]);
    const selectedEventId = ref(""); // 選択されたイベントID
    const cmbEvent = ref<HTMLSelectElement | null>(null);
    const agCode = ref(""); // AGコード
    const userId = ref(""); // ユーザーID
    const isLoading = ref(false);
    const { logoutUser } = useUserStore();

    // 初期処理
    onMounted(async () => {
      // 処理中フラグON
      processingStore.isProcessing = true;

      try {
        // 初期処理時はTrue
        isInitError.value = true;
        isLoading.value = true;
        // ローカルストレージからユーザー情報を取得して内部変数に展開
        const userInfo = localStorage.getItem(AppConstants.USER_SESSION_NAME);

        if (userInfo) {
          const parsedUserInfo = JSON.parse(userInfo);
          agCode.value = parsedUserInfo.agCode;
          userId.value = parsedUserInfo.userId;
        }

        // 初期表示データ取得API
        const response = await apiAuthClient.post(
          "/api/EventEntryDownload/getInitialDisplayData",
          {
            systemKbn: AppConstants.SYSTEM_KBN_WITH,
            userId: userId.value,
            agCode: agCode.value,
            targetDate: getTargetDate(""),
          }
        );

        eventDataList.value = response.data.eventListData;

        // フォーカス設定
        if (cmbEvent.value) {
          cmbEvent.value.focus();
        }

        isInitError.value = false;
        isDataFetched.value = true; // データが取得されたことを設定
      } finally {
        // 処理中フラグOFF
        processingStore.isProcessing = false;
        isLoading.value = false;
      }
    });

    /** ダウンロード処理 */
    const onDownloadClick = async () => {
      // なにか処理中の場合は何もしない
      if (processingStore.isProcessing) {
        return;
      }

      // 処理中フラグON
      processingStore.isProcessing = true;

      try {
        errorMessage.value = "";

        // 必須チェック
        if (!selectedEventId.value) {
          errorMessage.value = formatMessage(messages.ERR_0003, "イベント");
          scrollToTop();
          return;
        }

        // トークン有効性チェック
        if ((await isTokenAlive()) == false) {
          // トークンが無効な場合はログイン画面へ
          logoutUser();
          alert(messages.ERR_USER001);
          router.push("/login");
          return;
        }

        isLoading.value = true;

        const response = await apiDownloadClient.post(
          "/api/EventEntryDownload/getEntryFile",
          {
            systemKbn: AppConstants.SYSTEM_KBN_WITH,
            userId: userId.value,
            agCode: agCode.value,
            eventId: selectedEventId.value,
          }
        );

        if (response.status === 204) {
          errorMessage.value = formatMessage(messages.ERR_0009, "申込み情報");
          return;
        }

        // 選択したイベントIDのファイル名を取得
        const fileName = getEventCsvFileName(selectedEventId.value);

        // Blob を生成し、ファイルをダウンロード
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        // ダウンロードAPIエラー
        outputDebugLogs("Error downloading files: " + error);
        alert(formatMessage(messages.ERR_SYS003, "ファイルダウンロード"));
      } finally {
        processingStore.isProcessing = false;
        isLoading.value = false;
      }
    };

    /**
     * イベント申込み情報CSVファイル名取得
     * @param eventId イベントID
     */
    const getEventCsvFileName = (eventId: string): string => {
      const event = eventDataList.value.find(
        (item) => item.eventId === eventId
      );
      return event?.eventCsvFileName || "";
    };

    /**
     * 戻るボタン押下
     */
    const onBack = () => {
      if (processingStore.isProcessing) {
        // 何か処理中なら処理をしない
        return;
      }

      try {
        processingStore.isProcessing = true;

        // 管理者トップへ遷移
        router.push("/kanrisya-top");
      } finally {
        processingStore.isProcessing = false;
      }
    };

    return {
      isInitError,
      isDataFetched,
      errorMessage,
      isKaunetAdmin,
      onDownloadClick,
      eventDataList,
      selectedEventId,
      cmbEvent,
      isLoading,
      onBack,
    };
  },
});
</script>
<style scoped>
.errorform {
  margin-top: 50px;
  width: 100%;
  height: 500px;
  text-align: center;
}
.midlleform {
  width: 900px;
}
.flex-container {
  display: flex;
}
.btn {
  margin: 0.25rem 0.125rem;
}

.btn-middle {
  margin: 0.5rem 0.55rem;
  padding: 0.375rem 2rem;
  border-radius: 1rem;
  width: 150px;
}

.button_right {
  text-align: center;
  margin-left: 700px;
  width: 150px;
}

.btn-footer {
  margin: 0.5rem 0.55rem;
  padding: 0.375rem 2rem;
  border-radius: 1rem;
  width: 143px;
}

.btn-footer-large {
  margin: 0.5rem 0.55rem;
  padding: 0.375rem 2rem;
  border-radius: 1rem;
  width: 170px;
}
.form-control-title {
  text-align: center;
  background-color: lightgrey;
  border-radius: 0.25rem;
}
.form-control {
  display: inline-block;
  vertical-align: middle;
}
select.form-control {
  -webkit-appearance: auto;
  appearance: auto;
}
</style>
